// OrderSuccess.jsx
// Confirmation page shown after a successful order.

import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { formatCurrency } from "../utils/pricing";
import { shortOrderId, formatDate } from "../utils/helpers";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    async function fetchOrder() {
      try {
        const snap = await getDoc(doc(db, "orders", orderId));
        if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.error(err);
      }
    }
    if (orderId) fetchOrder();
  }, [orderId]);

  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      {/* Success icon */}
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Order Placed!</h1>
      <p className="text-gray-500 mb-2">Thank you for shopping with Generic Ideas 🎉</p>

      {order && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mt-6 text-left space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Order ID</span>
            <span className="font-mono font-bold text-gray-900">{shortOrderId(order.id)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Date</span>
            <span className="text-gray-700">{formatDate(order.createdAt)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Items</span>
            <span className="text-gray-700">{order.items?.length} product{order.items?.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payment</span>
            <span className="capitalize text-gray-700">{order.paymentMethod === "cod" ? "Cash on Delivery" : order.paymentMethod}</span>
          </div>

          {order.isCouponApplied && (
            <>
              <div className="flex justify-between text-sm border-t border-gray-100 pt-3">
                <span className="text-gray-500">Original Amount</span>
                <span className="text-gray-900 font-medium line-through decoration-gray-400">
                  {formatCurrency(order.pricing?.total + order.pricing?.discount)}
                </span>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-xl p-3 my-2 space-y-1 transition-all">
                <div className="flex justify-between text-sm">
                  <span className="text-green-600 font-medium">Coupon Applied</span>
                  <span className="font-bold text-green-700 uppercase tracking-wide">{order.coupon?.couponCode}</span>
                </div>
                <div className="flex justify-between text-sm font-bold">
                  <span className="text-green-600">Savings</span>
                  <span className="text-green-700">-{formatCurrency(order.pricing?.discount)}</span>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between text-sm font-bold text-lg border-t border-gray-100 pt-3">
            <span>Total Paid</span>
            <span className="text-red-600">{formatCurrency(order.pricing?.total)}</span>
          </div>
        </div>
      )}

      <div className="flex gap-3 mt-8 justify-center">
        <Link
          to="/orders"
          className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors text-sm"
        >
          View My Orders
        </Link>
        <Link
          to="/"
          className="px-6 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors text-sm"
        >
          Continue Shopping
        </Link>
      </div>
    </div>
  );
}
